import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Mail, MessageSquare, Send, Users, Check, X, AlertCircle,
  Loader2, ChevronDown, ChevronUp, User, Phone
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SEGMENT_INFO = {
  vip: {
    title: 'VIP Customers',
    description: 'High-value customers loved by therapists',
    color: 'purple',
    defaultSubject: 'Exclusive VIP Offer from WellNest Wellness',
    defaultMessage: `Dear {name},

As one of our most valued VIP guests, we're delighted to offer you an exclusive experience!

✨ Special VIP Package - 20% Off
💆 Premium therapy sessions
🎁 Complimentary add-ons

Book now and enjoy the WellNest difference.

Call us or reply to schedule your appointment.

Warm regards,
WellNest Wellness Team`
  },
  loyal: {
    title: 'Loyal Customers',
    description: 'Regular customers who keep coming back',
    color: 'blue',
    defaultSubject: 'Thank You for Your Loyalty - Special Reward Inside!',
    defaultMessage: `Dear {name},

Thank you for being a loyal guest at WellNest Wellness!

🎉 As a token of our appreciation:
• 15% off your next visit
• Free upgrade to premium therapy
• Priority booking slots

Your loyalty means the world to us.

Reply to book your rewarding session!

With gratitude,
WellNest Wellness Team`
  },
  at_risk: {
    title: 'At-Risk Customers',
    description: 'Customers who may need attention',
    color: 'amber',
    defaultSubject: 'We Miss You at WellNest Wellness!',
    defaultMessage: `Dear {name},

It's been a while since your last visit, and we truly miss you!

🌟 Come back and enjoy:
• 25% welcome-back discount
• Complimentary consultation
• New therapies just for you

We'd love to see you again and make your experience even better.

Simply reply to this message to book.

Warmly,
WellNest Wellness Team`
  },
  problematic: {
    title: 'Attention Required',
    description: 'Customers flagged by therapists',
    color: 'red',
    defaultSubject: 'Your Feedback Matters - WellNest Wellness',
    defaultMessage: `Dear {name},

At WellNest Wellness, your satisfaction is our priority.

We noticed your recent experiences may not have met expectations. We'd love the opportunity to make things right.

📞 Please reach out to us directly
🎁 Complimentary session on us
💬 Share your feedback

Your comfort and satisfaction matter most to us.

Sincerely,
WellNest Wellness Management`
  },
  new: {
    title: 'New Customers',
    description: 'First-time visitors',
    color: 'green',
    defaultSubject: 'Welcome to WellNest Wellness! 🌿',
    defaultMessage: `Dear {name},

Welcome to the WellNest family! 🙏

We're thrilled you chose us for your wellness journey.

🎁 New Customer Benefits:
• 10% off your first package
• Free wellness consultation
• Member-only offers

Book your next session and experience complete relaxation.

Looking forward to serving you!

Warmly,
WellNest Wellness Team`
  }
};

const CampaignComposer = ({ 
  isOpen, 
  onClose, 
  segment, 
  customers,
  channel // 'email' or 'whatsapp'
}) => {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [sending, setSending] = useState(false);
  const [showRecipients, setShowRecipients] = useState(false);
  const [configStatus, setConfigStatus] = useState({ email: false, whatsapp: false });

  useEffect(() => {
    if (isOpen && segment) {
      // Set defaults based on segment
      const info = SEGMENT_INFO[segment] || SEGMENT_INFO.new;
      setSubject(info.defaultSubject);
      setMessage(info.defaultMessage);
      
      // Select all customers by default
      setSelectedCustomers(customers.map(c => c.phone || c.name));
      
      // Check configuration status
      checkConfig();
    }
  }, [isOpen, segment, customers]);

  const checkConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/campaigns/check-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfigStatus({
        email: response.data.email?.configured || false,
        whatsapp: response.data.whatsapp?.configured || false
      });
    } catch (error) {
      console.error('Error checking config:', error);
    }
  };

  const toggleCustomer = (identifier) => {
    setSelectedCustomers(prev => 
      prev.includes(identifier)
        ? prev.filter(c => c !== identifier)
        : [...prev, identifier]
    );
  };

  const selectAll = () => {
    setSelectedCustomers(customers.map(c => c.phone || c.name));
  };

  const deselectAll = () => {
    setSelectedCustomers([]);
  };

  const getSelectedRecipients = () => {
    return customers.filter(c => selectedCustomers.includes(c.phone || c.name));
  };

  const handleSend = async () => {
    const recipients = getSelectedRecipients();
    
    if (recipients.length === 0) {
      toast.error('Please select at least one recipient');
      return;
    }

    if (channel === 'email' && !subject.trim()) {
      toast.error('Please enter an email subject');
      return;
    }

    if (!message.trim()) {
      toast.error('Please enter a message');
      return;
    }

    // Check if channel is configured
    if (channel === 'email' && !configStatus.email) {
      toast.error('Email is not configured. Please add RESEND_API_KEY to your environment.');
      return;
    }

    if (channel === 'whatsapp' && !configStatus.whatsapp) {
      toast.error('WhatsApp is not configured. Please configure it in Settings → WhatsApp Configuration.');
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const endpoint = channel === 'email' ? '/campaigns/send-email' : '/campaigns/send-whatsapp';
      
      const payload = {
        segment,
        message,
        recipients: recipients.map(c => ({
          name: c.name,
          phone: c.phone,
          email: c.email
        }))
      };

      if (channel === 'email') {
        payload.subject = subject;
      }

      const response = await axios.post(`${API}${endpoint}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Campaign started! Sending to ${recipients.length} recipients...`);
      onClose();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to send campaign';
      toast.error(errorMsg);
    } finally {
      setSending(false);
    }
  };

  const segmentInfo = SEGMENT_INFO[segment] || SEGMENT_INFO.new;
  const selectedCount = selectedCustomers.length;
  const totalCount = customers.length;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-serif">
            {channel === 'email' ? (
              <Mail className="w-5 h-5 text-blue-600" />
            ) : (
              <MessageSquare className="w-5 h-5 text-green-600" />
            )}
            Send {channel === 'email' ? 'Email' : 'WhatsApp'} Campaign
          </DialogTitle>
          <DialogDescription>
            Send to {segmentInfo.title} ({selectedCount}/{totalCount} selected)
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Configuration Warning */}
          {((channel === 'email' && !configStatus.email) || 
            (channel === 'whatsapp' && !configStatus.whatsapp)) && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">
                  {channel === 'email' ? 'Email' : 'WhatsApp'} not configured
                </p>
                <p className="text-sm text-amber-700">
                  {channel === 'email' 
                    ? 'Add RESEND_API_KEY to environment to send emails.' 
                    : 'Configure WhatsApp in Settings → WhatsApp Configuration.'}
                </p>
              </div>
            </div>
          )}

          {/* Recipients Section */}
          <Collapsible open={showRecipients} onOpenChange={setShowRecipients}>
            <CollapsibleTrigger asChild>
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg cursor-pointer hover:bg-muted transition-colors">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-muted-foreground" />
                  <span className="font-medium">Recipients ({selectedCount}/{totalCount})</span>
                  <Badge variant={segmentInfo.color === 'purple' ? 'default' : 'secondary'}>
                    {segmentInfo.title}
                  </Badge>
                </div>
                {showRecipients ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </div>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="mt-2 p-3 border rounded-lg space-y-2 max-h-48 overflow-y-auto">
                <div className="flex gap-2 mb-2">
                  <Button size="sm" variant="outline" onClick={selectAll}>Select All</Button>
                  <Button size="sm" variant="outline" onClick={deselectAll}>Deselect All</Button>
                </div>
                {customers.map((customer, idx) => {
                  const identifier = customer.phone || customer.name;
                  const isSelected = selectedCustomers.includes(identifier);
                  return (
                    <div 
                      key={idx}
                      className={`flex items-center justify-between p-2 rounded ${isSelected ? 'bg-green-50 border border-green-200' : 'bg-muted/30'}`}
                    >
                      <div className="flex items-center gap-2">
                        <Checkbox 
                          checked={isSelected}
                          onCheckedChange={() => toggleCustomer(identifier)}
                        />
                        <div>
                          <p className="font-medium text-sm">{customer.name}</p>
                          <p className="text-xs text-muted-foreground flex items-center gap-2">
                            {customer.phone && <span><Phone className="w-3 h-3 inline" /> {customer.phone}</span>}
                            {customer.email && <span><Mail className="w-3 h-3 inline" /> {customer.email}</span>}
                          </p>
                        </div>
                      </div>
                      {isSelected && <Check className="w-4 h-4 text-green-600" />}
                    </div>
                  );
                })}
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Email Subject (only for email) */}
          {channel === 'email' && (
            <div>
              <Label>Subject Line</Label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Enter email subject..."
              />
            </div>
          )}

          {/* Message */}
          <div>
            <Label>Message</Label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Enter your message..."
              rows={10}
              className="font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Use {'{name}'} to personalize with customer name
            </p>
          </div>

          {/* Preview */}
          <div className="p-3 bg-muted/30 rounded-lg">
            <p className="text-xs text-muted-foreground mb-1">Preview (first recipient):</p>
            <div className="text-sm whitespace-pre-wrap bg-white p-3 rounded border">
              {message.replace('{name}', customers[0]?.name || 'Customer')}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-between items-center pt-4 border-t">
          <p className="text-sm text-muted-foreground">
            {selectedCount} recipient{selectedCount !== 1 ? 's' : ''} will receive this {channel === 'email' ? 'email' : 'message'}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={sending}>
              Cancel
            </Button>
            <Button 
              onClick={handleSend} 
              disabled={sending || selectedCount === 0}
              className={channel === 'email' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'}
            >
              {sending ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" /> Send {channel === 'email' ? 'Emails' : 'Messages'}</>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CampaignComposer;
